import { BankFeedsModule } from '@repo/accounts/BankFeedsModule'
import { BankFeedsTransactionsModule } from '@repo/accounts/BankFeedsTransactionsModule'
import BankFeedsReviewQueue from '../pages/accounts/bank-feeds-review-queue'
import { ClientsTableModule } from '@repo/clients-matters-tables/ClientsTableModule'
import { DocumentBundlesModule } from '@repo/document-bundles/DocumentBundlesModule'
import { ExpensesOvertimeModule } from '@repo/claims-tables/ExpensesOvertimeModule'
import { Fragment } from 'react'
import { KeyDatesTableModule } from '@repo/key-dates-table/KeyDatesTableModule'
import { ExpensesOvertimePermissions } from '../pages/claims-tables/expenses-overtime-permissions'
import { MattersTableModule } from '@repo/clients-matters-tables/MattersTableModule'
import { PageErrorBoundary } from '@repo/app-assets/PageErrorBoundary'
import { RedirectToOsprey1Module } from '@repo/redirect-to-osprey1/RedirectToOsprey1Module'
import { RootLayout } from '@repo/app-assets/RootLayout'
import HmrcItsaSetup from '../pages/configuration/hmrc-itsa/hmrc-itsa-setup'
import HmrcItsaCategoryMapping from '../pages/configuration/hmrc-itsa/hmrc-itsa-category-mapping'
import ItsaMtdQuarterly from '../pages/reports/itsa-mtd-quarterly'
import ItsaMtdAnnual from '../pages/reports/itsa-mtd-annual'
import ItsaMtdReport from '../pages/reports/itsa-mtd-report'
import BankReconciliationStatus from '../pages/supervisor/bank-reconciliation-status'
import BundledocsBundleSetup from '../pages/configuration/bundledocs/bundle-setup'
import ChangeMatter from '../pages/change-matter'
import ClientWorkspace from '../pages/workspaces/client-workspace'
import ContactManager from '../pages/contacts-organisations/contact-manager'
import ContractWorkFormsEditForm from '../pages/contract-form-forms/contract-work-forms-edit-form'
import CustomDataPages from '../pages/workspaces/custom-data-pages'
import EmailConfigurationComplete from '../pages/configuration/microsoft/email'
import EmailConfigurationSetup from '../pages/configuration/microsoft/email-setup'
import EmailSyncConfiguration from '../pages/supervisor/email-sync-configuration'
import ExpensesReview from '../pages/expenses-overtime-review/expenses-review'
import FormEvoCallbackPage from '../pages/configuration/third-party-forms/formevo-callback'
import FormEvoConfiguration from '../pages/supervisor/formevo-configuration'
import LicenceControl from '../pages/supervisor/licence-control'
import OvertimeReview from '../pages/expenses-overtime-review/overtime-review'
import ThirdPartyForms from '../pages/configuration/third-party-forms/third-party-forms'
import Home from '../pages'
import Login from '../pages/auth/login'
import Logout from '../pages/auth/logout'
import MatterWorkspace from '../pages/workspaces/matter-workspace'
import OrganisationManager from '../pages/contacts-organisations/organisation-manager'
import OutlookConfigurationComplete from '../pages/configuration/microsoft/outlook'
import OutlookConfigurationSetup from '../pages/configuration/microsoft/outlook-setup'
import PerfectPortalCaseTypeMapping from '../pages/case-type-mapping/perfect-portal-case-type-mapping'
import RequestPasswordReset from '../pages/auth/request-password-reset'
import ResetPassword from '../pages/auth/reset-password'
import Support from '../pages/support'
import ThirdPartyFormsTable from '../pages/third-party-forms-table/third-party-forms-table'
import UndertakingsTable from '../pages/undertakings/undertakings-table'

/*
  Each route below can have various properties:
    path: required string - the URL path for the route. The path shown for each route is relative to the parent route.
    id: string - a unique identifier for the route
    menuItemId: required number - the ID of the menu item that Osprey 1 uses to identify and authorise pages. This is used to map the React route to the Osprey 1 url
    overrideAspxPageName: string - for the iframe to show a different page than the default Osprey 1 page
    aspxUrlParams: array - parameters to pass to the aspx page when the route is accessed, and to help identify the page when navigating to it within the iframe
    redirectTo: string - the path to redirect to when the route is accessed
    element: React component - the component to render for the route
    pageDescription: string - a description of the page content which shows as a tooltip in the PageTitleBlock component
    pageIsMatterSpecific: boolean - controls whether the PageTitleBlock shows details for the current matter
    hideStatusBar: boolean - suppresses the workflow StatusBar on a matter-specific page that would otherwise show it
    fixedHeight: boolean - controls the height of the iframe when rendering the Osprey 1 page
    children: array - an array of child routes
*/

const routes = [
  {
    path: '/',
    id: 'root',
    overrideAspxPageName: 'matterpage.aspx',
    aspxUrlParams: [{ key: 'isHome', value: 'true' }],
    menuItemId: 10,
    iframeVisible: false,
    element: <RootLayout />,
    children: [
      {
        path: '',
        id: 'home',
        iframeVisible: false,
        overrideAspxPageName: 'matterpage.aspx',
        aspxUrlParams: [{ key: 'isHome', value: 'true' }],
        menuItemId: 10,
        element: <Home />
      },
      {
        path: 'change-matter/:clientMatterNo',
        menuItemId: 1200,
        overrideAspxPageName: 'matterpage.aspx',
        iframeVisible: false,
        element: <ChangeMatter />,
        aspxUrlParams: [{ key: 'redirectTo', value: '*' }],
        pageTitleOverride: ' '
      },
      {
        path: 'case-management',
        menuItemId: 1000,
        redirectTo: 'case-management/matters/matter-workspace',
        pageDescription: 'View complete information for the selected matter',
        pageIsMatterSpecific: true
      },
      {
        path: 'configuration/microsoft/email-setup',
        menuItemId: 10,
        iframeVisible: false,
        element: <EmailConfigurationSetup />,
        pageTitleOverride: 'Microsoft email setup'
      },
      {
        path: 'configuration/microsoft/email',
        menuItemId: 10,
        iframeVisible: false,
        element: <EmailConfigurationComplete />,
        pageTitleOverride: 'Microsoft email setup complete'
      },
      {
        path: 'configuration/microsoft/outlook-setup',
        menuItemId: 10,
        iframeVisible: false,
        element: <OutlookConfigurationSetup />,
        pageTitleOverride: 'Microsoft Outlook setup'
      },
      {
        path: 'configuration/microsoft/outlook',
        menuItemId: 10,
        iframeVisible: false,
        element: <OutlookConfigurationComplete />,
        pageTitleOverride: 'Microsoft Outlook setup complete'
      },
      {
        path: 'configuration/bundledocs/bundle-setup',
        menuItemId: 10,
        iframeVisible: false,
        element: <BundledocsBundleSetup />,
        pageTitleOverride: 'Bundledocs authentication'
      },
      {
        path: 'configuration/third-party-forms',
        menuItemId: 10,
        iframeVisible: false,
        element: <ThirdPartyForms />,
        pageTitleOverride: 'Third party forms'
      },
      {
        path: 'configuration/third-party-forms/formevo',
        menuItemId: 10,
        iframeVisible: false,
        element: <FormEvoCallbackPage />,
        pageTitleOverride: 'Third party forms'
      },
      {
        path: 'supervisor/financial-setup/hmrc-itsa-setup',
        menuItemId: 14232,
        iframeVisible: false,
        element: <HmrcItsaSetup />,
        pageTitleOverride: 'HMRC MTD ITSA Setup'
      },
      {
        path: 'supervisor/financial-setup/hmrc-itsa-category-mapping',
        menuItemId: 14231,
        iframeVisible: false,
        element: <HmrcItsaCategoryMapping />,
        pageTitleOverride: 'Configure MTD ITSA category mappings'
      },
      {
        path: 'search',
        menuItemId: 1287,
        pageTitleOverride: 'Search'
      },
      {
        path: 'search-form',
        menuItemId: 1280,
        pageTitleOverride: 'Search'
      },
      {
        path: 'search/client-list',
        menuItemId: 1281,
        pageTitleOverride: 'Search',
        overrideAspxPageName: 'clientlist.aspx'
      },
      {
        path: 'search/matter-history',
        menuItemId: 3350,
        pageTitleOverride: 'Search',
        overrideAspxPageName: 'matthistsearch.aspx'
      },
      {
        path: 'search/search-results',
        menuItemId: 1285,
        pageTitleOverride: 'Search results'
      },
      {
        path: 'search/search-results/matter-history',
        menuItemId: 3990,
        fixedHeight: true,
        pageTitleOverride: 'Matter history search',
        overrideAspxPageName: 'MatterHistory.aspx',
        aspxUrlParams: [
          { key: 'isSearch', value: 'true' },
          { key: 'search', value: '*' }
        ]
      },
      {
        path: 'case-management/matters',
        menuItemId: 1200,
        pageDescription: 'View all matters',
        iframeVisible: false,
        element: <MattersTableModule />
      },
      {
        path: 'case-management/matters/matter-dashboard',
        menuItemId: 1200,
        pageDescription: 'View complete information for the selected matter',
        pageIsMatterSpecific: true,
        redirectTo: 'case-management/matters/matter-workspace'
      },
      {
        path: 'case-management/matters/matter-workspace',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected matter',
        element: <MatterWorkspace />,
        aspxUrlParams: [
          { key: 'edt', value: '*' },
          { key: 'isf2cerr', value: 'False' },
          { key: 'flag', value: '0' }
        ]
      },
      {
        path: 'case-management/matters/matter-workspace/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected matter',
        element: <MatterWorkspace />,
        aspxUrlParams: [
          { key: 'edt', value: '*' },
          { key: 'isf2cerr', value: 'False' },
          { key: 'flag', value: '0' }
        ]
      },
      {
        path: 'case-management/matters/matter-page/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        pageDescription: 'View complete information for the selected matter',
        aspxUrlParams: [{ key: 'viewextensions', value: 'true' }]
      },
      // OS-2715/OS-2716/OS-2717 (defects 56/57/58): laaCertificateGrid.aspx and
      // tbillallopoa.aspx have no menu-tree registration at all (checked directly on the
      // bench, 2026-09-30 -- zero rows in dbo.accflag/dbo.accareaflag for either page), so
      // menuItemId-driven resolution (the mechanism this file uses for pages like
      // matter-detail/edit-matter, which do have a real flag) can't reach them --
      // overrideAspxPageName is required, the same mechanism the root route already uses
      // for matterpage.aspx?isHome=true. No iframeVisible: false on any of these three --
      // the whole point is to show the legacy content, not hide it behind React's own UI
      // the way matter-workspace does. :clientMatterNo (no aspxUrlParams needed for
      // ClientNo/MatterNo) auto-populates them via reactIframeLoop.js's own
      // CLIENT_MATTER_PARAM_MAP -- ASP.NET's QueryString lookup is case-insensitive, so
      // ClientNo/MatterNo also satisfy each page's own client_no/matter_no reads.
      {
        path: 'case-management/matters/matter-workspace/certificates/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        pageDescription: 'Manage Legal Aid certificates for this client',
        overrideAspxPageName: 'laaCertificateGrid.aspx'
      },
      {
        path: 'case-management/matters/matter-workspace/poa-interim-claim/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        pageDescription: 'POA / interim claim selection for this matter',
        overrideAspxPageName: 'tbillallopoa.aspx'
      },
      // OS-2715 (defect 62, round 3): laaCertificateGrid.aspx's own New/Edit actions
      // (CreateBtn_Click, the grid's context-menu Edit) are a full server Response.Redirect
      // to laaCertificateDetail.aspx -- a page with no route of its own at all. With no
      // candidate's own aspxPageName matching that page, onIframeLoad's matcher fell through
      // to scoring every other menuItemId:1200 sibling, and legal-aid-classic (line below)
      // won purely on its Area/Flag aspxUrlParams -- values every flag-1200 navigation
      // carries generically, not a signal specific to that route -- rewriting the iframe src
      // to matterpage.aspx mid-form (analysis.md's traced mechanism). Registering this page
      // (still flag 1200, no accflag/accareaflag row -- same pattern as its siblings above)
      // gives it a genuine aspxPageNameMatch so it wins outright.
      {
        path: 'case-management/matters/matter-workspace/certificates/detail/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        pageDescription: 'Add or edit a Legal Aid certificate for this client',
        overrideAspxPageName: 'laaCertificateDetail.aspx'
      },
      // OS-2717 (defect 58): bridges to matterPage.aspx's own Certificate:/Escape Status:/
      // Fixed Fee:/Export CostsMaster CSV fields, which are not reimplemented in React --
      // a dedicated route rather than reusing matter-page (line above), whose own
      // aspxUrlParams/viewextensions shape is tied to its existing postLoadAction-specific
      // uses (restore matter, send email/SMS), not a persistent, user-browsable view.
      {
        path: 'case-management/matters/matter-workspace/legal-aid-classic/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        pageDescription: 'Certificate, Escape Status and CostsMaster export details for this matter (classic Osprey view)',
        overrideAspxPageName: 'matterpage.aspx',
        aspxUrlParams: [
          { key: 'Area', value: '1000' },
          { key: 'Flag', value: '1200' }
        ]
      },
      {
        path: 'case-management/matters/matter-detail/:clientMatterNo?',
        menuItemId: 1210,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/matter-detail/edit-matter',
        menuItemId: 1211,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/matter-detail/edit-matter/:clientMatterNo?',
        menuItemId: 1211,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/matter-archive',
        menuItemId: 1213,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/matter-archive/:clientMatterNo?',
        menuItemId: 1213,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/matter-history',
        menuItemId: 3990,
        pageIsMatterSpecific: true,
        pageDescription: 'Matter correspondence',
        fixedHeight: true,
        overrideAspxPageName: 'MatterHistory.aspx'
      },
      {
        path: 'case-management/matters/matter-history/:clientMatterNo?',
        menuItemId: 3990,
        pageIsMatterSpecific: true,
        pageDescription: 'Matter correspondence',
        fixedHeight: true
      },
      {
        path: 'case-management/matters/recent-matters',
        menuItemId: 1283,
        pageDescription: 'Recently access matters'
      },
      {
        path: 'case-management/matters/matter-summary',
        menuItemId: 1290,
        pageIsMatterSpecific: true,
        pageDescription: 'Review ledger and WIP summaries'
      },
      {
        path: 'case-management/matters/matter-summary/credit-limit-warnings',
        menuItemId: 1291,
        pageDescription: 'Credit limits set against the matter'
      },
      {
        path: 'case-management/matters/key-dates',
        menuItemId: 3100,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        pageDescription: 'View key dates recorded against the matter',
        element: <KeyDatesTableModule isMatterSpecific={true} />,
        iframeVisible: false
      },
      {
        path: 'case-management/matters/key-dates/:clientMatterNo?',
        menuItemId: 3100,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        pageDescription: 'View key dates recorded against the matter',
        element: <KeyDatesTableModule isMatterSpecific={true} />,
        iframeVisible: false
      },
      {
        path: 'case-management/matters/document-bundles',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        pageDescription: 'View document bundles for the matter',
        element: <DocumentBundlesModule />,
        iframeVisible: false
      },
      {
        path: 'case-management/matters/document-bundles/:clientMatterNo?',
        menuItemId: 1200,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        pageDescription: 'View document bundles for the matter',
        element: <DocumentBundlesModule />,
        iframeVisible: false
      },
      {
        path: 'case-management/matters/key-dates/key-date-detail',
        menuItemId: 3110,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/custom-matter-views',
        menuItemId: 1500,
        pageIsMatterSpecific: true,
        pageDescription: 'View custom matter data'
      },
      {
        path: 'case-management/matters/custom-matter-views/run',
        menuItemId: 1501,
        pageIsMatterSpecific: true,
        pageDescription: 'View custom matter data',
        overrideAspxPageName: 'qsinternal.aspx'
      },
      {
        path: 'case-management/matters/custom-matter-views/data-defaults',
        menuItemId: 1502,
        pageIsMatterSpecific: true,
        pageDescription: 'View custom matter data'
      },
      {
        path: 'case-management/matters/custom-questionnaires',
        menuItemId: 1600,
        pageIsMatterSpecific: true,
        pageDescription: 'Enter custom matter data'
      },
      {
        path: 'case-management/matters/custom-data-pages',
        menuItemId: 3200,
        pageIsMatterSpecific: true,
        iframeVisible: false,
        element: <CustomDataPages type="matter" />,
        pageDescription: 'View and enter custom matter data'
      },
      {
        path: 'case-management/matters/custom-data-pages/multi-entry',
        menuItemId: 3200,
        pageIsMatterSpecific: true,
        pageDescription: 'View and enter custom matter data',
        overrideAspxPageName: 'dossierpageedit.aspx',
        aspxUrlParams: [
          { key: 'pageid', value: '*' },
          { key: 'allowmultipleentries', value: 'true' }
        ]
      },
      {
        path: 'case-management/matters/custom-data-pages/add-edit-custom-data-page',
        menuItemId: 3210,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/document-production',
        menuItemId: 3400,
        pageIsMatterSpecific: true,
        pageDescription: 'Produce correspondence and forms'
      },
      {
        path: 'case-management/matters/document-production/:clientMatterNo?',
        menuItemId: 3400,
        pageIsMatterSpecific: true,
        pageDescription: 'Produce correspondence and forms'
      },
      {
        path: 'case-management/matters/workflows',
        menuItemId: 3600,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'workflowclient.aspx',
        pageDescription: 'Tasks that contain actions related to the matter'
      },
      {
        path: 'case-management/matters/workflows/remove',
        menuItemId: 3601,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'WorkflowRemoveSelection.aspx'
      },
      {
        path: 'case-management/matters/workflows/edit',
        menuItemId: 3610,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'workflowlist.aspx'
      },
      {
        path: 'case-management/matters/workflows/advanced',
        menuItemId: 3615,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'matttaskadvanced.aspx'
      },
      {
        path: 'case-management/matters/workflows/delete-tasks',
        menuItemId: 3616,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'mtadvDelete.aspx'
      },
      {
        path: 'case-management/matters/workflows/undo-tasks',
        menuItemId: 3617,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'mtAdvUndo.aspx'
      },
      {
        path: 'case-management/matters/workflows/load',
        menuItemId: 3620,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'workflowload.aspx'
      },
      {
        path: 'case-management/matters/workflows/view',
        menuItemId: 3621,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'WorkflowView.aspx'
      },
      {
        path: 'case-management/matters/workflows/run',
        menuItemId: 3630,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'runtasks2.aspx'
      },
      {
        path: 'case-management/matters/workflows/run/:clientMatterNo?',
        menuItemId: 3630,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'runtasks2.aspx'
      },
      {
        path: 'case-management/matters/contract-work-forms',
        menuItemId: 3800,
        pageIsMatterSpecific: true,
        pageDescription: 'Capture CRM / civil data'
      },
      {
        path: 'case-management/matters/contract-work-forms/edit-form-data/investigations',
        menuItemId: 3810,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'ContractworkPageEdit.aspx',
        iframeVisible: false,
        element: <ContractWorkFormsEditForm type="cdsi" />,
        aspxUrlParams: [{ key: 'page', value: 'cdsi', client: '*', matter: '*' }]
      },
      {
        path: 'case-management/matters/contract-work-forms/edit-form-data/proceedings',
        menuItemId: 3810,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'ContractworkPageEdit.aspx',
        iframeVisible: false,
        element: <ContractWorkFormsEditForm type="cdspr" />,
        aspxUrlParams: [{ key: 'page', value: 'cdspr', client: '*', matter: '*' }]
      },
      {
        path: 'case-management/matters/contract-work-forms/edit-form-data/appeals-and-reviews',
        menuItemId: 3810,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'ContractworkPageEdit.aspx',
        iframeVisible: false,
        element: <ContractWorkFormsEditForm type="cdsar" />,
        aspxUrlParams: [{ key: 'page', value: 'cdsar', client: '*', matter: '*' }]
      },
      {
        path: 'case-management/matters/contract-work-forms/edit-form-data/prison-law',
        menuItemId: 3810,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'ContractworkPageEdit.aspx',
        iframeVisible: false,
        element: <ContractWorkFormsEditForm type="cdspl" />,
        aspxUrlParams: [{ key: 'page', value: 'cdspl', client: '*', matter: '*' }]
      },
      {
        path: 'case-management/matters/contract-work-forms/edit-form-data',
        menuItemId: 3810,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'ContractworkPageEdit.aspx'
      },
      {
        path: 'case-management/matters/lexis-forms',
        menuItemId: 3943,
        pageIsMatterSpecific: true,
        pageDescription: 'Produce Lexis forms related to the matter'
      },
      {
        path: 'case-management/matters/infotrack',
        menuItemId: 19499,
        pageIsMatterSpecific: true,
        pageDescription: 'Submit InfoTrack searches'
      },
      {
        path: 'case-management/matters/infotrack/infotrack-order-details',
        menuItemId: 19500,
        overrideAspxPageName: 'InfoTrackOrderDetails.aspx'
      },
      {
        path: 'case-management/matters/infotrack/eCOS',
        menuItemId: 19513,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'eCOS' }]
      },
      {
        path: 'case-management/matters/infotrack/AML',
        menuItemId: 19514,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'AML' }]
      },
      {
        path: 'case-management/matters/infotrack/lms-redemption',
        menuItemId: 19515,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'LMSRedemption' }]
      },
      {
        path: 'case-management/matters/infotrack/search',
        menuItemId: 19516,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'Search' }]
      },
      {
        path: 'case-management/matters/infotrack/quick-quote',
        menuItemId: 19517,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'QuickQuote' }]
      },
      {
        path: 'case-management/matters/infotrack/contract-pack-TA13',
        menuItemId: 19518,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'ContractPack/TA13' }]
      },
      {
        path: 'case-management/matters/infotrack/official-copies',
        menuItemId: 19519,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'OfficialCopies' }]
      },
      {
        path: 'case-management/matters/infotrack/bankruptcy',
        menuItemId: 19520,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'Bankruptcy' }]
      },
      {
        path: 'case-management/matters/infotrack/os-priority',
        menuItemId: 19521,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'OSPriority' }]
      },
      {
        path: 'case-management/matters/infotrack/aviva',
        menuItemId: 19522,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'Aviva' }]
      },
      {
        path: 'case-management/matters/infotrack/CLS',
        menuItemId: 19523,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'CLS' }]
      },
      {
        path: 'case-management/matters/infotrack/sign-it',
        menuItemId: 19524,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'SignIT' }]
      },
      {
        path: 'case-management/matters/infotrack/property-enquiries',
        menuItemId: 19525,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'PropertyEnquiries' }]
      },
      {
        path: 'case-management/matters/infotrack/TR1-TP1',
        menuItemId: 19526,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'TR1/TP1' }]
      },
      {
        path: 'case-management/matters/infotrack/lawyer-bank-check',
        menuItemId: 19528,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'LawyerBankCheck' }]
      },
      {
        path: 'case-management/matters/infotrack/client-bank-check',
        menuItemId: 19529,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'ClientBankCheck' }]
      },
      {
        path: 'case-management/matters/infotrack/lender-handbook',
        menuItemId: 19530,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'LenderHandbook' }]
      },
      {
        path: 'case-management/matters/infotrack/MR01',
        menuItemId: 19531,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'MR01' }]
      },
      {
        path: 'case-management/matters/infotrack/SDLT',
        menuItemId: 19532,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'SDLT' }]
      },
      {
        path: 'case-management/matters/infotrack/AP1',
        menuItemId: 19533,
        pageIsMatterSpecific: true,
        aspxUrlParams: [{ key: 'RedirectPath', value: 'AP1' }]
      },
      {
        path: 'case-management/matters/infotrack/quick-links',
        menuItemId: 19534,
        pageIsMatterSpecific: true
      },
      {
        path: 'case-management/matters/searchflow',
        menuItemId: 3930,
        pageIsMatterSpecific: true,
        pageDescription: 'Submit Searchflow orders'
      },
      {
        path: 'case-management/matters/searchflow/submit-search',
        menuItemId: 3931,
        overrideAspxPageName: 'SearchFlowSubmitSearch.aspx'
      },
      {
        path: 'case-management/matters/searchflow/order-update',
        menuItemId: 3932,
        overrideAspxPageName: 'SearchflowOrderUpdate.aspx'
      },
      {
        path: 'case-management/matters/searchflow/property-details',
        menuItemId: 3933,
        overrideAspxPageName: 'SearchFlowPropertyPlan.aspx'
      },
      {
        path: 'case-management/matters/searchflow/submit-response',
        menuItemId: 14494,
        overrideAspxPageName: 'SearchflowSubmitResponse.aspx'
      },
      {
        path: 'case-management/matters/searchflow/view-plan',
        menuItemId: 14495,
        overrideAspxPageName: 'SearchflowOrderViewPlan.aspx'
      },
      {
        path: 'case-management/matters/undertakings',
        pageDescription: 'View and manage undertakings for the matter',
        menuItemId: 1200,
        iframeVisible: false,
        pageIsMatterSpecific: true,
        element: <UndertakingsTable />
      },
      {
        path: 'case-management/matters/third-party-forms',
        menuItemId: 3960,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        iframeVisible: false,
        pageDescription: 'View third-party forms recorded against the matter',
        element: <ThirdPartyFormsTable />
      },
      {
        path: 'case-management/matters/third-party-forms/:clientMatterNo?',
        menuItemId: 3960,
        pageIsMatterSpecific: true,
        hideStatusBar: true,
        iframeVisible: false,
        pageDescription: 'View third-party forms recorded against the matter',
        element: <ThirdPartyFormsTable />
      },
      {
        path: 'case-management/clients',
        menuItemId: 1100,
        iframeVisible: false,
        element: <ClientsTableModule />
      },
      {
        path: 'case-management/clients/client-workspace',
        menuItemId: 1100,
        pageIsClientSpecific: true,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected client',
        element: <ClientWorkspace />,
        aspxUrlParams: [
          { key: 'edt', value: '*' },
          { key: 'isf2cerr', value: 'False' }
        ]
      },
      {
        path: 'case-management/clients/client-workspace/:clientMatterNo?',
        menuItemId: 1100,
        pageIsClientSpecific: true,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected client',
        element: <ClientWorkspace />,
        aspxUrlParams: [
          { key: 'edt', value: '*' },
          { key: 'isf2cerr', value: 'False' }
        ]
      },
      {
        path: 'case-management/clients/client-page/:clientMatterNo',
        menuItemId: 1100,
        pageIsClientSpecific: true,
        pageDescription: 'View complete information for the selected client',
        overrideAspxPageName: 'clientpage.aspx',
        aspxUrlParams: [{ key: 'viewextensions', value: 'true' }]
      },
      {
        path: 'case-management/clients/client-detail/edit-client',
        menuItemId: 1110,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-detail/edit-client/:clientMatterNo?',
        menuItemId: 1110,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-detail/individual/:clientMatterNo?',
        menuItemId: 1110,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-detail/company/:clientMatterNo?',
        menuItemId: 1120,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-workspace/add-client-phone',
        menuItemId: 1161,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-workspace/edit-client-phone',
        menuItemId: 1162,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/client-workspace/delete-client-phone',
        menuItemId: 1163,
        pageIsClientSpecific: true
      },
      {
        path: 'case-management/clients/custom-data-pages',
        menuItemId: 3200,
        pageIsClientSpecific: true,
        iframeVisible: false,
        element: <CustomDataPages type="client" />,
        pageDescription: 'View and enter custom client data',
        aspxUrlParams: [{ key: 'doss', value: 'client' }]
      },
      {
        path: 'case-management/clients/matter-history',
        menuItemId: 3990,
        pageIsClientSpecific: true,
        pageDescription: 'Matter correspondence',
        fixedHeight: true,
        aspxUrlParams: [{ key: 'global', value: 'true' }]
      },
      {
        path: 'case-management/clients/client-portal',
        pageIsClientSpecific: true,
        menuItemId: 1130,
        redirectTo: 'case-management/clients'
      },
      {
        path: 'case-management/clients/client-portal/enable-client-portal',
        menuItemId: 1131,
        pageIsClientSpecific: true,
        pageDescription: 'Enable client access to the client portal'
      },
      {
        path: 'case-management/clients/client-portal/enable-associate-portal',
        menuItemId: 1134,
        pageIsClientSpecific: true,
        pageDescription: 'Enable associate access to the client portal'
      },
      {
        path: 'case-management/client-matter-search',
        menuItemId: 1280,
        pageDescription: 'Search clients and matters',
        overrideAspxPageName: 'searchdetail.aspx',
        aspxUrlParams: [
          { key: 'key1', value: 'matter' },
          { key: 'key2', value: 'Yes' },
          { key: 'uid', value: '' },
          { key: 'edt', value: '1' },
          { key: 'returnurl', value: 'matterpage.aspx' }
        ]
      },
      {
        path: 'case-management/client-matter-search/search-results',
        menuItemId: 1285,
        pageDescription: 'Search clients and matters',
        overrideAspxPageName: 'searchresult.aspx',
        aspxUrlParams: [
          { key: 'key1', value: 'matter' },
          { key: 'key2', value: 'Yes' },
          { key: 'returnUrl', value: 'matterpage.aspx' }
        ]
      },
      {
        path: 'case-management/all-key-dates',
        menuItemId: 3140,
        pageDescription: 'View all key dates within filters set',
        element: <KeyDatesTableModule />,
        iframeVisible: false
      },
      {
        path: 'case-management/searchflow-favourite-products',
        menuItemId: 12004,
        pageDescription: 'Searchflow products marked as favourites'
      },
      {
        path: 'case-management/label-printing',
        menuItemId: 1220,
        pageDescription: 'Print a label for your matter'
      },
      {
        path: 'case-management/conditional-fees',
        menuItemId: 1230,
        pageIsMatterSpecific: true,
        pageDescription: 'Record conditional fees against a matter'
      },
      {
        path: 'case-management/batch-archive',
        menuItemId: 1240,
        pageDescription: 'Archive matters in bulk'
      },
      {
        path: 'case-management/contacts-organisations',
        menuItemId: 3500,
        pageDescription: 'Shows all contacts and organisations in the address book'
      },
      {
        path: 'case-management/contacts-organisations/contact-manager',
        menuItemId: 3510,
        pageDescription: 'Shows all contacts in the address book',
        element: <ContactManager />,
        iframeVisible: false
      },
      {
        path: 'case-management/contacts-organisations/organisation-manager',
        menuItemId: 3520,
        pageDescription: 'Shows all organisations in the address book',
        element: <OrganisationManager />,
        iframeVisible: false
      },
      {
        path: 'case-management/contacts-organisations/organisation-manager/organisation-link',
        menuItemId: 3525,
        overrideAspxPageName: 'organisationLink.aspx',
        pageDescription: 'Link an organisation'
      },
      {
        path: 'case-management/web-client-enquiries',
        menuItemId: 1400,
        pageDescription: 'Enquiries raised by prospective clients for review',
        overrideAspxPageName: 'qspendinggrid.aspx'
      },
      {
        path: 'case-management/web-client-enquiries/view',
        pageTitleOverride: 'View Web Client Enquiry',
        menuItemId: 1401,
        pageDescription: 'Enquiries raised by prospective clients for review',
        overrideAspxPageName: 'qsPendingView.aspx',
        aspxUrlParams: [
          { key: 'cvid', value: '*' },
          { key: 'wktype', value: '*' }
        ]
      },
      {
        path: 'case-management/questionnaires-for-review',
        menuItemId: 1402,
        pageDescription: 'Questionnaires submitted by clients for review',
        overrideAspxPageName: 'qsPostQuestionnaires.aspx'
      },
      {
        path: 'case-management/questionnaires-for-review/edit',
        pageTitleOverride: 'Edit Questionnaire for Review',
        menuItemId: 1403,
        pageDescription: 'Questionnaires submitted by clients for review',
        overrideAspxPageName: 'qsEditPosting.aspx',
        aspxUrlParams: [
          { key: 'cvid', value: '*' },
          { key: 'wktype', value: '*' },
          { key: 'srcarea', value: '*' },
          { key: 'clino', value: '*' },
          { key: 'matno', value: '*' }
        ]
      },
      {
        path: 'case-management/questionnaires-for-review/post',
        pageTitleOverride: 'Post Questionnaires for Review',
        menuItemId: 1404,
        pageDescription: 'Questionnaires submitted by clients for review',
        overrideAspxPageName: 'qsPostData.aspx',
        aspxUrlParams: [
          { key: 'cvid', value: '*' },
          { key: 'wktype', value: '*' },
          { key: 'defer', value: '*' },
          { key: 'user', value: '*' }
        ]
      },
      {
        path: 'case-management/general-file-history',
        menuItemId: 84,
        fixedHeight: true
      },
      {
        path: 'case-management/general-file-history-templates',
        menuItemId: 80
      },
      {
        path: 'compliance',
        menuItemId: 11000
      },
      {
        path: 'compliance/oaths',
        menuItemId: 11001,
        pageDescription: 'Store oath records',
        fixedHeight: true
      },
      {
        path: 'compliance/storage',
        menuItemId: 11002,
        pageDescription: 'Store other documents',
        fixedHeight: true
      },
      {
        path: 'compliance/fee-sharing-referrals',
        menuItemId: 11003,
        pageDescription: 'Store fee sharing and referral agreements',
        fixedHeight: true
      },
      {
        path: 'compliance/introductions-third-parties',
        menuItemId: 11004,
        pageDescription: 'Store third party introduction info',
        fixedHeight: true
      },
      {
        path: 'compliance/equality-diversity-breaches',
        menuItemId: 11005,
        pageDescription: 'Store equality & diversity breach info',
        fixedHeight: true
      },
      {
        path: 'compliance/gdpr',
        menuItemId: 1310
      },
      {
        path: 'compliance/gdpr/erasure-requests',
        menuItemId: 1311
      },
      {
        path: 'compliance/gdpr/erasure-requests-approval',
        menuItemId: 1313
      },
      {
        path: 'compliance/gdpr/data-requests',
        menuItemId: 1314
      },
      {
        path: 'compliance/gdpr/data-requests-approval',
        menuItemId: 1316
      },
      {
        path: 'accounts',
        menuItemId: 5000,
        redirectTo: 'accounts/client-ledger',
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger',
        menuItemId: 5000,
        pageIsMatterSpecific: true,
        pageDescription: 'A list of financial transactions for the matter'
      },
      {
        path: 'accounts/client-ledger/post-client-receipt',
        menuItemId: 5001,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-client-payment',
        menuItemId: 5002,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/cost-transfer-client-to-office-disbs',
        menuItemId: 5003,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/cost-transer-office-disbs-to-client',
        menuItemId: 5004,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-office-payment',
        menuItemId: 5005,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-office-receipt',
        menuItemId: 5006,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-petty-cash-receipt',
        menuItemId: 5007,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-petty-cash-payment',
        menuItemId: 5008,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-unpaid-disbursements',
        menuItemId: 5009,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/pay-unpaid-disbursements',
        menuItemId: 5010,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/cancel-unpaid-disbursements',
        menuItemId: 5011,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-bill',
        menuItemId: 5012,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-credit-note',
        menuItemId: 5013,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-provisional-bill',
        menuItemId: 5014,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/deliver-provisional-bill',
        menuItemId: 5015,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/cancel-provisional-bill',
        menuItemId: 5016,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/enter-counsel-fees',
        menuItemId: 5017,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/pay-counsel-fees',
        menuItemId: 5018,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/cancel-counsel-fees',
        menuItemId: 5019,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-deposit-receipt',
        menuItemId: 5020,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-deposit-withdrawal',
        menuItemId: 5021,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/write-off-debit',
        menuItemId: 5022,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/write-off-credit',
        menuItemId: 5023,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/transfer-to-nominal',
        menuItemId: 5024,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/transfer-from-nominal',
        menuItemId: 5025,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/client-transfer-receipt',
        menuItemId: 5026,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/client-transfer-payment',
        menuItemId: 5027,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/office-transfer-receipt',
        menuItemId: 5028,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/office-transfer-payment',
        menuItemId: 5029,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/client-transfer-to-deposit',
        menuItemId: 5030,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/client-transfer-from-deposit',
        menuItemId: 5031,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/disbursements-to-office-transfer',
        menuItemId: 5032,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/office-to-disbursements-transfer',
        menuItemId: 5033,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/post-note',
        menuItemId: 5034,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/office-cheque-received',
        menuItemId: 5035,
        pageIsMatterSpecific: true
      },
      {
        path: 'accounts/client-ledger/fee-earner-bill-calculator',
        pageIsMatterSpecific: true,
        menuItemId: 5040
      },
      {
        path: 'accounts/client-ledger/select-posting-id',
        pageIsMatterSpecific: true,
        menuItemId: 5041
      },

      {
        path: 'accounts/client-ledger/post-entry',
        pageIsMatterSpecific: true,
        menuItemId: 5100
      },
      {
        path: 'accounts/client-ledger/undo-posting',
        pageIsMatterSpecific: true,
        menuItemId: 5120
      },
      {
        path: 'accounts/client-ledger/allocate-payments',
        menuItemId: 5200,
        pageIsMatterSpecific: true,
        pageDescription: 'Assign payments and disbursements to bills'
      },
      {
        path: 'accounts/client-ledger/requisition',
        menuItemId: 5300,
        pageIsMatterSpecific: true,
        pageDescription: 'A request for a posting to be made to the client ledger'
      },
      {
        path: 'accounts/client-ledger/requisition/:clientMatterNo?',
        menuItemId: 5300,
        pageIsMatterSpecific: true,
        pageDescription: 'A request for a posting to be made to the client ledger'
      },
      {
        path: 'accounts/client-ledger/interest-calculator',
        menuItemId: 5400,
        pageIsMatterSpecific: true,
        pageDescription: `Track interest due on monies held in client’s accounts`
      },
      {
        path: 'accounts/client-ledger/interest-calculator/print',
        menuItemId: 5401,
        pageIsMatterSpecific: true,
        pageDescription: `Print interest due on monies held in client’s accounts`
      },
      {
        path: 'accounts/client-ledger/deposit-link',
        menuItemId: 5500,
        pageIsMatterSpecific: true,
        pageDescription: 'Link designated deposit accounts to matters'
      },
      {
        path: 'accounts/client-ledger/deposit-link/add-deposit-account',
        menuItemId: 5501,
        pageIsMatterSpecific: true,
        pageDescription: 'Link designated deposit accounts to matters'
      },
      {
        path: 'accounts/client-ledger/print',
        menuItemId: 5600,
        pageIsMatterSpecific: true,
        pageDescription: 'Print the client ledger'
      },
      {
        path: 'accounts/client-ledger/ledger-summary',
        menuItemId: 5700,
        pageIsMatterSpecific: true,
        pageDescription: 'Balance and time summary for the matter'
      },
      {
        path: 'accounts/client-ledger/credit-limit-warnings',
        menuItemId: 5710,
        pageIsMatterSpecific: true,
        pageDescription: 'Credit limits set against the matter'
      },
      {
        path: 'accounts/client-ledger/auto-posting',
        menuItemId: 5800,
        pageDescription: 'Upload a csv file to post data'
      },
      {
        path: 'accounts/client-ledger/custom-auto-posting',
        menuItemId: 5801,
        pageDescription: 'Upload a csv file and select posting type to post data'
      },
      {
        path: 'accounts/billing',
        menuItemId: 2200,
        redirectTo: 'accounts/billing/print-bills'
      },
      {
        path: 'accounts/billing/run-batch-aged-debtor-letters',
        menuItemId: 4311
      },
      {
        path: 'accounts/billing/print-bills',
        menuItemId: 2220,
        pageDescription: 'Print bills that have been raised'
      },
      {
        path: 'accounts/billing/view-billed-time',
        menuItemId: 2230,
        pageIsMatterSpecific: true,
        pageDescription: 'View time billed on the matter'
      },
      {
        path: 'accounts/billing/billing-guide',
        menuItemId: 2240,
        pageIsMatterSpecific: true,
        pageDescription: 'Produce a billing guide including time posted and unbilled disbursements'
      },
      {
        path: 'accounts/nominal-ledgers',
        menuItemId: 6000,
        redirectTo: 'accounts/nominal-ledgers/nominal-ledgers'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-ledgers',
        menuItemId: 6100,
        overrideAspxPageName: 'nominalpage.aspx'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-ledgers/search',
        menuItemId: 6100,
        pageDescription: 'Search nominal ledgers',
        overrideAspxPageName: 'searchdetail.aspx',
        aspxUrlParams: [{ key: 'key2', value: 'nominal' }]
      },
      {
        path: 'accounts/nominal-ledgers/nominal-header',
        menuItemId: 6110,
        pageDescription: 'A list of nominal accounts',
        fixedHeight: true,
        overrideAspxPageName: 'accountheadergrid.aspx'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-header/add-nominal-header',
        menuItemId: 6111,
        pageDescription: 'Add a new nominal header'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-header/edit-nominal-header',
        menuItemId: 6111,
        pageDescription: 'Edit a nominal header',
        aspxUrlParams: [{ key: 'key1', value: 'n' }]
      },
      {
        path: 'accounts/nominal-ledgers/nominal-header/list',
        menuItemId: 6114,
        fixedHeight: true,
        aspxUrlParams: [
          { key: 'key1', value: 'nominal' },
          { key: 'returnurl', value: 'nominalpage.aspx' }
        ]
      },
      {
        path: 'accounts/nominal-ledgers/nominal-sac',
        menuItemId: 6120,
        pageDescription: 'A list of sub analysis codes against the selected nominal'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-sac/add-nominal-sac',
        menuItemId: 6121,
        pageDescription: 'Add a sub analysis code'
      },
      {
        path: 'accounts/nominal-ledgers/nominal-sac/edit-nominal-sac',
        menuItemId: 6122,
        pageDescription: 'Edit a sub analysis code'
      },
      {
        path: 'accounts/nominal-ledgers/post-entry',
        menuItemId: 6130
      },
      {
        path: 'accounts/nominal-ledgers/undo-posting',
        menuItemId: 6140
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings',
        menuItemId: 6150,
        pageDescription: 'Postings for regular payments, receipts or transfers'
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/add-schedule',
        menuItemId: 6151
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/edit-schedule',
        menuItemId: 6152,
        aspxUrlParams: [{ key: 'key1', value: '*' }]
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/delete-schedule',
        menuItemId: 6153
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/pending-items',
        menuItemId: 6154
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/post',
        menuItemId: 6155
      },
      {
        path: 'accounts/nominal-ledgers/scheduled-postings/print-pending-schedule-items',
        menuItemId: 6156
      },
      {
        path: 'accounts/nominal-ledgers/print-current-ledger',
        menuItemId: 6160,
        pageDescription: 'Print the selected nominal ledger'
      },
      {
        path: 'accounts/nominal-ledgers/print-nominal-ledger',
        menuItemId: 6161,
        pageDescription: 'Print the nominal ledger'
      },
      {
        path: 'accounts/nominal-ledgers/print-all-ledgers',
        menuItemId: 6163,
        pageDescription: 'Print all nominal ledger'
      },
      {
        path: 'accounts/nominal-ledgers/budgets',
        menuItemId: 6200,
        pageDescription: 'Produce a budget report'
      },
      {
        path: 'accounts/nominal-ledgers/budgets/add-budget',
        menuItemId: 6210
      },
      {
        path: 'accounts/nominal-ledgers/budgets/edit-budget',
        menuItemId: 6220
      },
      {
        path: 'accounts/nominal-ledgers/requisition',
        menuItemId: 6300,
        pageDescription: 'A request for a posting to be made to a nominal ledger'
      },
      {
        path: 'accounts/purchase-ledgers',
        menuItemId: 7000,
        redirectTo: 'accounts/purchase-ledgers/purchase-ledgers'
      },
      {
        path: 'accounts/purchase-ledgers/purchase-ledgers',
        menuItemId: 7000,
        pageDescription: 'Post to the purchase ledgers',
        overrideAspxPageName: 'purchasepage.aspx'
      },
      {
        path: 'accounts/purchase-ledgers/purchase-ledgers/search',
        menuItemId: 7000,
        pageDescription: 'Search purchase ledgers',
        overrideAspxPageName: 'searchdetail.aspx',
        aspxUrlParams: [{ key: 'key2', value: 'PurLedge' }]
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header',
        menuItemId: 7100,
        pageDescription: 'A list of purchase ledgers',
        fixedHeight: true,
        overrideAspxPageName: 'accountheadergrid.aspx'
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header/list',
        menuItemId: 7000,
        fixedHeight: true,
        overrideAspxPageName: 'accountlist.aspx',
        aspxUrlParams: [
          // there are two keys for the same value here, because Osprey 1 likes to play games
          { key: 'key1', value: 'PurLedge' },
          { key: 'key1', value: 'purchase' },
          { key: 'returnurl', value: 'purchasepage.aspx' }
        ]
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header/add-purchase-header',
        menuItemId: 7110,
        fixedHeight: true
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header/edit-purchase-header',
        menuItemId: 7110,
        fixedHeight: true,
        aspxUrlParams: [{ key: 'key1', value: 'p' }]
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header/post-entry',
        menuItemId: 7200
      },
      {
        path: 'accounts/purchase-ledgers/purchase-header/undo-posting',
        menuItemId: 7300
      },
      {
        path: 'accounts/purchase-ledgers/allocate-payments',
        menuItemId: 7400,
        pageDescription: 'Allocate payments to a supplier invoice'
      },
      {
        path: 'accounts/purchase-ledgers/print',
        menuItemId: 7500,
        pageDescription: 'Print the purchase ledgers'
      },
      {
        path: 'accounts/purchase-ledgers/print-ledger',
        menuItemId: 7510
      },
      {
        path: 'accounts/purchase-ledgers/requisition',
        menuItemId: 7600,
        pageDescription: 'View all pending requisitions'
      },
      {
        path: 'accounts/banks-journals',
        menuItemId: 8000,
        redirectTo: 'accounts/banks-journals/banks-and-journals'
      },
      {
        path: 'accounts/banks-journals/bank-feeds',
        menuItemId: 8210,
        element: <BankFeedsModule />,
        iframeVisible: false,
        aspxUrlParams: [{ key: 'identifier', value: 'bankfeeds' }]
      },
      {
        path: 'accounts/banks-journals/bank-feeds/:plaidBankAccountId?',
        menuItemId: 8210,
        element: <BankFeedsTransactionsModule />,
        iframeVisible: false,
        aspxUrlParams: [{ key: 'identifier', value: 'bankfeeds-detail' }]
      },
      {
        path: 'accounts/banks-journals/bank-feeds/:plaidBankAccountId/review-queue',
        menuItemId: 8320,
        iframeVisible: false,
        element: <BankFeedsReviewQueue />,
        pageTitleOverride: 'Bank feed review queue'
      },
      {
        path: 'accounts/banks-journals/post-entry',
        menuItemId: 8100
      },
      {
        path: 'accounts/banks-journals/view-undo-posting',
        menuItemId: 8110
      },
      {
        path: 'accounts/banks-journals/banks-and-journals',
        menuItemId: 8210,
        pageDescription: 'View balances for banks and journals'
      },
      {
        path: 'accounts/banks-journals/reconcile-bank',
        menuItemId: 8300
      },
      {
        path: 'accounts/banks-journals/group-ticking',
        menuItemId: 8310
      },
      {
        path: 'accounts/banks-journals/auto-reconciliation',
        menuItemId: 8320,
        pageDescription: 'Perform automatic bank reconciliation'
      },
      {
        path: 'accounts/banks-journals/auto-reconciliation/print',
        menuItemId: 8321
      },
      {
        path: 'accounts/banks-journals/requisitions',
        menuItemId: 8400,
        pageDescription: 'View all pending requisitions',
        fixedHeight: true
      },
      {
        path: 'accounts/banks-journals/requisitions/authorise',
        menuItemId: 8410
      },
      {
        path: 'accounts/banks-journals/requisitions/post-client-requisition',
        menuItemId: 8420
      },
      {
        path: 'accounts/banks-journals/requisitions/print-requisition',
        menuItemId: 8430
      },
      {
        path: 'accounts/banks-journals/cheque-printing',
        menuItemId: 8500,
        pageDescription: 'Print cheques that have been spooled for printing'
      },
      {
        path: 'accounts/employee-claims/expense-claims',
        menuItemId: 14120,
        element: <ExpensesOvertimeModule isSubmissionVersion={false} selectedTab="expenses" />,
        iframeVisible: false
      },
      {
        path: 'accounts/employee-claims/expense-claims/expense-review',
        menuItemId: 14120,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected expense claim',
        element: <ExpensesReview />
      },
      {
        path: 'accounts/employee-claims/expense-claims/expense-review/:expenseClaimId?',
        menuItemId: 14120,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected expense claim',
        element: <ExpensesReview />
      },
      {
        path: 'accounts/employee-claims/expense-claims/permissions',
        menuItemId: 14130,
        iframeVisible: false,
        pageDescription: 'View and edit permissions for expense claims',
        element: <ExpensesOvertimePermissions isExpenses={true} />
      },
      {
        path: 'accounts/employee-claims/overtime-claims',
        menuItemId: 14220,
        element: <ExpensesOvertimeModule isSubmissionVersion={false} selectedTab="overtime" />,
        iframeVisible: false
      },
      {
        path: 'accounts/employee-claims/overtime-claims/overtime-review',
        menuItemId: 14220,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected overtime claim',
        element: <OvertimeReview />
      },
      {
        path: 'accounts/employee-claims/overtime-claims/overtime-review/:overtimeClaimId?',
        menuItemId: 14220,
        iframeVisible: false,
        pageDescription: 'View complete information for the selected overtime claim',
        element: <OvertimeReview />
      },
      {
        path: 'accounts/employee-claims/overtime-claims/permissions',
        menuItemId: 14230,
        iframeVisible: false,
        pageDescription: 'View and edit permissions for overtime claims',
        element: <ExpensesOvertimePermissions isExpenses={false} />
      },
      {
        path: 'reports',
        menuItemId: 4000
      },
      {
        path: 'reports/report-viewer',
        menuItemId: 9910,
        overrideAspxPageName: 'reportviewer.aspx'
      },
      {
        path: 'reports/financial-management',
        menuItemId: 4100
      },
      {
        path: 'reports/financial-management/trial-balance',
        menuItemId: 4110
      },
      {
        path: 'reports/financial-management/profit-loss',
        menuItemId: 4120
      },
      {
        path: 'reports/financial-management/period-end',
        menuItemId: 4130
      },
      {
        path: 'reports/financial-management/aged-creditors',
        menuItemId: 4150
      },
      {
        path: 'reports/financial-management/balance-sheet',
        menuItemId: 4160
      },
      {
        path: 'reports/financial-management/budget-analysis',
        menuItemId: 4170
      },
      {
        path: 'reports/financial-management/office-receipts',
        menuItemId: 4180
      },
      {
        path: 'reports/financial-management/office-receipts-allocated',
        menuItemId: 4181
      },
      {
        path: 'reports/financial-management/year-end',
        menuItemId: 4190
      },
      {
        path: 'reports/financial-management/cash-accounting-vat-received',
        menuItemId: 4220
      },
      {
        path: 'reports/other-financial',
        menuItemId: 4500
      },
      {
        path: 'reports/other-financial/audit-trail',
        menuItemId: 4510
      },
      {
        path: 'reports/other-financial/audit-trail-nl-pl',
        menuItemId: 4520
      },
      {
        path: 'reports/other-financial/purchase-ledger-allocations',
        menuItemId: 4530
      },
      {
        path: 'reports/other-financial/purchase-ledger-balances',
        menuItemId: 4540
      },
      {
        path: 'reports/other-financial/suggested-pl-payments',
        menuItemId: 4550
      },
      {
        path: 'reports/other-financial/unposted-audit-trail',
        menuItemId: 4560
      },
      {
        path: 'reports/other-financial/credit-limit-warnings',
        menuItemId: 4230
      },
      {
        path: 'reports/other-financial/current-credit-limit-warnings',
        menuItemId: 4260
      },
      {
        path: 'reports/other-financial/batch-interest-calculation',
        menuItemId: 4240
      },
      {
        path: 'reports/other-financial/cheque-report',
        menuItemId: 4250
      },
      {
        path: 'reports/other-financial/multiple-deposit-ledger-balances',
        menuItemId: 4210
      },
      {
        path: 'reports/vat-mtd',
        menuItemId: 19496
      },
      {
        path: 'reports/itsa-mtd-quarterly',
        menuItemId: 14232,
        iframeVisible: false,
        element: <ItsaMtdQuarterly />,
        pageTitleOverride: 'MTD ITSA Quarterly Submission'
      },
      {
        path: 'reports/itsa-mtd-annual',
        menuItemId: 14232,
        iframeVisible: false,
        element: <ItsaMtdAnnual />,
        pageTitleOverride: 'MTD ITSA Annual Submission'
      },
      {
        path: 'reports/itsa-mtd-report',
        menuItemId: 14232,
        iframeVisible: false,
        element: <ItsaMtdReport />,
        pageTitleOverride: 'MTD ITSA Report'
      },
      {
        path: 'reports/client-financial',
        menuItemId: 4300
      },
      {
        path: 'reports/client-financial/aged-debtors',
        menuItemId: 4310
      },
      {
        path: 'reports/client-financial/bills-rendered',
        menuItemId: 4330
      },
      {
        path: 'reports/client-financial/cost-transfer',
        menuItemId: 4340
      },
      {
        path: 'reports/client-financial/ledger-balance',
        menuItemId: 4350
      },
      {
        path: 'reports/client-financial/client-reconciliation',
        menuItemId: 4140
      },
      {
        path: 'reports/client-financial/unpaid-bills',
        menuItemId: 4370
      },
      {
        path: 'reports/client-financial/unpaid-disbursements',
        menuItemId: 4380
      },
      {
        path: 'reports/client-financial/bills-paid-by-fee-earner',
        menuItemId: 4390
      },
      {
        path: 'reports/client-financial/fees-journal-listing',
        menuItemId: 4391
      },
      {
        path: 'reports/client-financial/case-cost',
        menuItemId: 4410
      },
      {
        path: 'reports/client-financial/case-cost-variance',
        menuItemId: 4420
      },
      {
        path: 'reports/client-financial/client-funds-movement',
        menuItemId: 4655
      },
      {
        path: 'reports/analysis',
        menuItemId: 4600
      },
      {
        path: 'reports/analysis/live-matters',
        menuItemId: 4610
      },
      {
        path: 'reports/analysis/new-matters-by-user',
        menuItemId: 4611
      },
      {
        path: 'reports/analysis/new-matters-report',
        menuItemId: 4612
      },
      {
        path: 'reports/analysis/completed-matters-by-user',
        menuItemId: 4613
      },
      {
        path: 'reports/analysis/archived-matters',
        menuItemId: 4620
      },
      {
        path: 'reports/analysis/completed-matters',
        menuItemId: 4621
      },
      {
        path: 'reports/analysis/key-dates',
        menuItemId: 4630
      },
      {
        path: 'reports/analysis/review-periods',
        menuItemId: 4640
      },
      {
        path: 'reports/analysis/fees-analysis',
        menuItemId: 4650
      },
      {
        path: 'reports/analysis/audit-trail-case-mgt',
        menuItemId: 4660
      },
      {
        path: 'reports/analysis/fees-analysis-apportioned-costs',
        menuItemId: 4670
      },
      {
        path: 'reports/analysis/analysis-costs-received',
        menuItemId: 4680
      },
      {
        path: 'reports/analysis/fees-analysis-time-fe',
        menuItemId: 4690
      },
      {
        path: 'reports/time',
        menuItemId: 4700
      },
      {
        path: 'reports/time/time-spent',
        menuItemId: 4710
      },
      {
        path: 'reports/time/time-spent-summary',
        menuItemId: 4720
      },
      {
        path: 'reports/time/current-month-wip-disbs',
        menuItemId: 4730
      },
      {
        path: 'reports/time/percentage-recovery',
        menuItemId: 4740
      },
      {
        path: 'reports/time/work-in-progress',
        menuItemId: 4750
      },
      {
        path: 'reports/time/wip-time-fe',
        menuItemId: 4760
      },
      {
        path: 'reports/time/attendance-note-matter',
        menuItemId: 4770
      },
      {
        path: 'reports/miscellaneous-reports',
        menuItemId: 4800
      },
      {
        path: 'reports/miscellaneous-reports/contract-work-reports',
        menuItemId: 4810
      },
      {
        path: 'reports/miscellaneous-reports/controlled-matter-start',
        menuItemId: 4811
      },
      {
        path: 'reports/miscellaneous-reports/consolidated-matter',
        menuItemId: 4812
      },
      {
        path: 'reports/miscellaneous-reports/crm6-11-contract-work',
        menuItemId: 4813,
        overrideAspxPageName: 'reportcds.aspx',
        pageTitleOverride: 'CRM 6 & CRM 11 contract work'
      },
      {
        path: 'reports/miscellaneous-reports/crm7-ns-fee-contract-work',
        menuItemId: 4814
      },
      {
        path: 'reports/miscellaneous-reports/dossier-report',
        menuItemId: 4820
      },
      {
        path: 'reports/miscellaneous-reports/spooled-archive-ledger',
        menuItemId: 4830
      },
      {
        path: 'reports/miscellaneous-reports/nominal-code-report',
        menuItemId: 4840
      },
      {
        path: 'reports/miscellaneous-reports/nominal-period-report',
        menuItemId: 4841
      },
      {
        path: 'reports/miscellaneous-reports/top-client-billing-report',
        menuItemId: 4850
      },
      {
        path: 'reports/miscellaneous-reports/top-client-new-matters',
        menuItemId: 4851
      },
      {
        path: 'reports/miscellaneous-reports/top-users-tasks-ticked',
        menuItemId: 4852
      },
      {
        path: 'reports/miscellaneous-reports/top-clients-wip-report',
        menuItemId: 4853
      },
      {
        path: 'reports/miscellaneous-reports/top-users-files-opened',
        menuItemId: 4854
      },
      {
        path: 'reports/miscellaneous-reports/top-users-letters-produced',
        menuItemId: 4855
      },
      {
        path: 'reports/miscellaneous-reports/auto-billing-report',
        menuItemId: 4860
      },
      {
        path: 'reports/miscellaneous-reports/client-list',
        menuItemId: 4870
      },
      {
        path: 'reports/miscellaneous-reports/performance-targets',
        menuItemId: 4880
      },
      {
        path: 'reports/miscellaneous-reports/last-movement-dates',
        menuItemId: 4890
      },
      {
        path: 'reports/miscellaneous-reports/restricted-matters',
        menuItemId: 4891,
        fixedHeight: true
      },
      {
        path: 'reports/miscellaneous-reports/matter-restriction-audit',
        menuItemId: 4892,
        fixedHeight: true
      },
      {
        path: 'reports/custom-reports',
        menuItemId: 4897,
        pageDescription: 'Find and run a custom report',
        fixedHeight: true
      },
      {
        path: 'reports/custom-reports/aged-work-in-progress',
        menuItemId: 4929
      },
      {
        path: 'reports/custom-reports/client-list',
        menuItemId: 4931
      },
      {
        path: 'reports/custom-reports/introducer-report',
        menuItemId: 4935
      },
      {
        path: 'reports/custom-reports/ate-report',
        menuItemId: 4938
      },
      {
        path: 'reports/custom-reports/client-list-report',
        menuItemId: 4940
      },
      {
        path: 'reports/custom-reports/client-analysis',
        menuItemId: 4945
      },
      {
        path: 'reports/custom-reports/custom-email-report',
        menuItemId: 4946
      },
      {
        path: 'supervisor',
        menuItemId: 9000
      },
      {
        path: 'supervisor/code-setup',
        menuItemId: 9100
      },
      {
        path: 'supervisor/code-setup/work-types',
        menuItemId: 9110,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/work-types/edit-work-types',
        menuItemId: 9111,
        overrideAspxPageName: 'worktypeDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/work-types/published-work-types',
        menuItemId: 9118,
        overrideAspxPageName: 'pubworktypeGrid.aspx'
      },
      {
        path: 'supervisor/code-setup/work-types/delete-published-work-types',
        menuItemId: 9126,
        overrideAspxPageName: 'pubworktypeDelete.aspx'
      },
      {
        path: 'supervisor/code-setup/work-types/published-work-types-detail',
        menuItemId: 9127,
        overrideAspxPageName: 'pubworktypeDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/work-types/published-work-types-edit',
        menuItemId: 9128,
        overrideAspxPageName: 'pubworktypeEdit.aspx'
      },
      {
        path: 'supervisor/code-setup/activity-codes',
        menuItemId: 9120,
        fixedHeight: true,
        overrideAspxPageName: 'rcodtypeGrid.aspx'
      },
      {
        path: 'supervisor/code-setup/activity-codes/add-activity-code',
        menuItemId: 9121,
        overrideAspxPageName: 'rcodtypeDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/activity-codes/edit-activity-code',
        menuItemId: 9121,
        overrideAspxPageName: 'rcodtypeDetail.aspx',
        aspxUrlParams: [
          { key: 'key1', value: 'P' },
          { key: 'key1', value: 'L' }
        ]
      },
      {
        path: 'supervisor/code-setup/activity-codes/link-activity',
        menuItemId: 9114,
        fixedHeight: true,
        overrideAspxPageName: 'worktypeLink.aspx'
      },
      {
        path: 'supervisor/code-setup/activity-codes/link-activity-data',
        menuItemId: 9119,
        overrideAspxPageName: 'worktypelinkdata.aspx'
      },
      {
        path: 'supervisor/code-setup/activity-codes/examine-linking',
        menuItemId: 9124,
        overrideAspxPageName: 'rcodtypelink.aspx'
      },
      {
        path: 'supervisor/code-setup/work-types/link-sub-category',
        menuItemId: 9129,
        overrideAspxPageName: 'SubCategoryLink.aspx'
      },
      {
        path: 'supervisor/code-setup/remuneration-types',
        menuItemId: 9130,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/remuneration-types/add-remuneration-type',
        menuItemId: 9131,
        overflow: 'rtypedetail.aspx'
      },
      {
        path: 'supervisor/code-setup/remuneration-types/link-activity-codes-data',
        menuItemId: 9136,
        overflow: 'rcodelinkdata.aspx'
      },
      {
        path: 'supervisor/code-setup/remuneration-types/link-activity-codes-view',
        menuItemId: 9137,
        overflow: 'rcodeLink.aspx'
      },
      {
        path: 'supervisor/code-setup/remuneration-types/link-activity-codes-delete',
        menuItemId: 9139,
        overflow: 'rcodeDelete.aspx'
      },
      {
        path: 'supervisor/code-setup/fee-earners',
        menuItemId: 9140,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/fee-earners/transfer-fee-earners',
        menuItemId: 19202,
        overrideAspxPageName: 'feeEarnerTransfer.aspx'
      },
      {
        path: 'supervisor/code-setup/fee-earners/add-edit-fee-earners',
        menuItemId: 9141
      },
      {
        path: 'supervisor/code-setup/fee-earners/link-remuneration-type-hourly',
        menuItemId: 9144
      },
      {
        path: 'supervisor/code-setup/fee-earners/link-activity-code-unitary',
        menuItemId: 9145
      },
      {
        path: 'supervisor/code-setup/fee-earners/link-activity-code',
        menuItemId: 9147
      },
      {
        path: 'supervisor/code-setup/groups',
        menuItemId: 9150,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/groups/add-group',
        menuItemId: 9151,
        overrideAspxPageName: 'grpcodeDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/franchise-categories',
        menuItemId: 9160,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/franchise-categories/add-franchise-category',
        menuItemId: 9161,
        overrideAspxPageName: 'francatdetail.aspx'
      },
      {
        path: 'supervisor/code-setup/standard-text-descriptions',
        menuItemId: 9170,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/standard-text-descriptions/add-standard-text-description',
        menuItemId: 9171,
        overrideAspxPageName: 'stantextDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/standard-text-descriptions/edit-standard-text-description',
        menuItemId: 9172,
        overrideAspxPageName: 'stantextEdit.aspx'
      },
      {
        path: 'supervisor/code-setup/standard-text-descriptions/delete-standard-text-description',
        menuItemId: 9173,
        overrideAspxPageName: 'stantextDelete.aspx'
      },
      {
        path: 'supervisor/code-setup/standard-text-descriptions/link-merge-fields',
        menuItemId: 9175,
        overrideAspxPageName: 'stantextLinkFields.aspx'
      },
      {
        path: 'supervisor/code-setup/attendance-notes',
        menuItemId: 9180,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/attendance-notes/add-attendance-note',
        menuItemId: 9181,
        overrideAspxPageName: 'atttextDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/fee-earner-status',
        menuItemId: 9190,
        fixedHeight: true
      },
      {
        path: 'supervisor/code-setup/fee-earner-status/add-fee-earner-status',
        menuItemId: 9191,
        overrideAspxPageName: 'festatusDetail.aspx'
      },
      {
        path: 'supervisor/code-setup/phases-tasks',
        menuItemId: 9194
      },
      {
        path: 'supervisor/code-setup/phases-tasks/add-phase-task-code',
        menuItemId: 9195,
        overrideAspxPageName: 'addEditPhaseTaskCode.aspx'
      },
      {
        path: 'supervisor/code-setup/activities',
        menuItemId: 9196
      },
      {
        path: 'supervisor/code-setup/activities/add-activity-code',
        menuItemId: 9197,
        overrideAspxPageName: 'addEditActivityCode.aspx'
      },
      {
        path: 'supervisor/code-setup/expenses',
        menuItemId: 9198
      },
      {
        path: 'supervisor/code-setup/expenses/add-expense',
        menuItemId: 9199,
        overrideAspxPageName: 'addEditExpense.aspx'
      },
      {
        path: 'supervisor/code-setup/matters-for-review',
        menuItemId: 19101,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup',
        menuItemId: 9200
      },
      {
        path: 'supervisor/system-setup/branches',
        menuItemId: 9210,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/branches/branch-details',
        menuItemId: 9211,
        overrideAspxPageName: 'branchDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/departments',
        menuItemId: 9220,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/departments/department-details',
        menuItemId: 9221,
        overrideAspxPageName: 'deptDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/labels-setup',
        menuItemId: 9230,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/labels-setup/add-template',
        menuItemId: 9231,
        overrideAspxPageName: 'labeltemplatedetail.aspx'
      },
      {
        path: 'supervisor/system-setup/labels-setup/edit-template',
        menuItemId: 9232,
        overrideAspxPageName: 'labeltemplatedetail.aspx'
      },
      {
        path: 'supervisor/system-setup/labels-setup/delete-template',
        menuItemId: 9233,
        overrideAspxPageName: 'labeltemplatedetail.aspx'
      },
      {
        path: 'supervisor/system-setup/matter-archive-details',
        menuItemId: 9240,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/system-settings',
        menuItemId: 9250
      },
      {
        path: 'supervisor/system-setup/system-settings/edit-system-settings',
        menuItemId: 9251,
        overrideAspxPageName: 'sysinfoDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/system-settings/practice-details',
        menuItemId: 9252,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/system-settings/practice-details/add-practice-details',
        menuItemId: 9253,
        overrideAspxPageName: 'practiceinfoDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/system-settings/third-party-data',
        menuItemId: 9254,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/system-settings/third-party-data/third-party-data-details',
        menuItemId: 9255,
        overrideAspxPageName: 'TPDataDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/communication-settings',
        menuItemId: 9260,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/communication-settings/add-communication-settings',
        menuItemId: 9261,
        overrideAspxPageName: 'phonetypeDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/communication-settings/link-designation',
        menuItemId: 9265,
        overrideAspxPageName: 'accuserLinkDesignation.aspx'
      },
      {
        path: 'supervisor/system-setup/users',
        menuItemId: 9270,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/users/access-templates/add-access-template',
        menuItemId: 9271
      },
      {
        path: 'supervisor/system-setup/users/access-templates/edit-access-template',
        menuItemId: 9272
      },
      {
        path: 'supervisor/system-setup/users/add-edit-users',
        menuItemId: 9274
      },
      {
        path: 'supervisor/system-setup/users/access-templates',
        menuItemId: 9276,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/users/select-temporary-access',
        menuItemId: 19270,
        overrideAspxPageName: 'accuserTemporaryAccess.aspx'
      },
      {
        path: 'supervisor/system-setup/users/request-rights',
        menuItemId: 9277
      },
      {
        path: 'supervisor/system-setup/users/request-rights/add-request-right',
        menuItemId: 9298,
        overrideAspxPageName: 'addRequestRight.aspx'
      },
      {
        path: 'supervisor/system-setup/users/edit-user-password',
        menuItemId: 9278
      },
      {
        path: 'supervisor/system-setup/users/financial-security-setup',
        menuItemId: 9279,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/users/login-manager',
        menuItemId: 9620
      },
      {
        path: 'supervisor/system-setup/ledger-view',
        menuItemId: 9280,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/ledger-view/add-ledger-view',
        menuItemId: 9281,
        overrideAspxPageName: 'userViewDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/ledger-view/edit-ledger-view',
        menuItemId: 9282,
        overrideAspxPageName: 'userViewDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/ledger-view/posting-type-view',
        menuItemId: 9284,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/ledger-view/edit-posting-type-view',
        menuItemId: 9285,
        overrideAspxPageName: 'ptypeviewdetail.aspx'
      },
      {
        path: 'supervisor/system-setup/ledger-view/financial-security-setup',
        menuItemId: 9286,
        overrideAspxPageName: 'accSecurityDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/configuration-settings',
        menuItemId: 9290,
        fixedHeight: true,
        overrideAspxPageName: 'switchesGrid.aspx'
      },
      {
        path: 'supervisor/system-setup/configuration-settings/edit-settings',
        menuItemId: 9291,
        overrideAspxPageName: 'switchesDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/configuration-settings/designation',
        menuItemId: 9292,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/configuration-settings/add-designation',
        menuItemId: 9293,
        overrideAspxPageName: 'DesignationDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/security-groups',
        menuItemId: 9296,
        fixedHeight: true
      },
      {
        path: 'supervisor/system-setup/security-groups/add-group',
        menuItemId: 9297,
        overrideAspxPageName: 'UsersAndSecurityGroupsDetail.aspx'
      },
      {
        path: 'supervisor/system-setup/email-setup',
        menuItemId: 9850
      },
      {
        path: 'supervisor/system-setup/email-sync-configuration',
        menuItemId: 9850,
        iframeVisible: false,
        element: <EmailSyncConfiguration />,
        pageTitleOverride: 'Email sync configuration'
      },
      {
        path: 'supervisor/system-setup/bank-reconciliation-status',
        menuItemId: 8320,
        iframeVisible: false,
        element: <BankReconciliationStatus />,
        pageTitleOverride: 'Bank reconciliation status'
      },
      {
        path: 'supervisor/system-setup/rebranding',
        menuItemId: 19201
      },
      // OS-2781: the new support/accounts permission (OS-2780) is represented purely as a
      // MenuItemArea grant (LicenceControlPermissions.SupportAccountsMenuItemAreaId = 9500),
      // not a traditional accflag-backed menu item -- following this app's own existing
      // precedent of using a bare MenuItemArea id directly as a route's menuItemId (e.g. the
      // 'supervisor' landing route itself above is gated on menuItemId: 9000, the Supervisor
      // area's own id, not a page-specific flag -- the same area id OS-2780 avoided reusing
      // for 9500, "that value collided with the live Supervisor area", technical-spec.md),
      // so the generic allowedMenuIds gate (useMenuItemsStore.js / RootLayout.jsx) hides both
      // this route and its nav entry for anyone not granted it.
      {
        path: 'supervisor/system-setup/licence-control',
        menuItemId: 9500,
        iframeVisible: false,
        element: <LicenceControl />,
        pageTitleOverride: 'Licence control'
      },
      {
        path: 'supervisor/report-type',
        menuItemId: 4898,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup',
        menuItemId: 9300
      },
      {
        path: 'supervisor/financial-setup/banks',
        menuItemId: 9310,
        fixedHeight: true,
        overrideAspxPageName: 'accountheaderGrid.aspx'
      },
      {
        path: 'supervisor/financial-setup/banks/add-bank',
        menuItemId: 9311,
        overrideAspxPageName: 'accountheaderdetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/banks/edit-bank',
        menuItemId: 9311,
        overrideAspxPageName: 'accountheaderdetail.aspx',
        aspxUrlParams: [{ key: 'key1', value: 'B' }]
      },
      {
        path: 'supervisor/financial-setup/cheques',
        menuItemId: 9320,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/cheques/setup-options',
        menuItemId: 9321,
        overrideAspxPageName: 'chqoptionDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/cheques/cheque-signatures',
        menuItemId: 9322,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/cheques/check-signatures/cheque-signature-details',
        menuItemId: 9909,
        overrideAspxPageName: 'chqsignaturedetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/cheques/cheque-templates',
        menuItemId: 9323,
        fixedHeight: true,

        overrideAspxPageName: 'chqtemplateGrid.aspx'
      },
      {
        path: 'supervisor/financial-setup/cheques/cheque-templates/cheque-template-detail',
        menuItemId: 9325,
        overrideAspxPageName: 'chqTemplateDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands',
        menuItemId: 9330,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands/add-band',
        menuItemId: 9331,
        overrideAspxPageName: 'icbandDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands/edit-band',
        menuItemId: 9332,
        overrideAspxPageName: 'icbandDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands/add-rate',
        menuItemId: 9334,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands/accrual-periods',
        menuItemId: 9337,
        overrideAspxPageName: 'accrueGrid.aspx',
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/interest-rates-bands/accrual-periods/accrual-period-detail',
        menuItemId: 9339,
        overrideAspxPageName: 'accrueDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts',
        menuItemId: 9350,
        fixedHeight: true,
        overrideAspxPageName: 'accountheaderGrid.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/add-deposit-account',
        menuItemId: 9351,
        overrideAspxPageName: 'accountheaderdetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/edit-deposit-account',
        menuItemId: 9351,
        overrideAspxPageName: 'accountheaderdetail.aspx',
        aspxUrlParams: [{ key: 'key1', value: 'D' }]
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/delete-link',
        menuItemId: 9352,
        overrideAspxPageName: 'deplinkdetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/link-to-client-edit',
        menuItemId: 9354,
        overrideAspxPageName: 'deplinkdetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/link-to-client-view',
        menuItemId: 9355,
        overrideAspxPageName: 'deplinkgrid.aspx'
      },
      {
        path: 'supervisor/financial-setup/deposit-accounts/link-to-client',
        menuItemId: 9356,
        overrideAspxPageName: 'deplinkmatters.aspx'
      },
      {
        path: 'supervisor/financial-setup/periods',
        menuItemId: 9360
      },
      {
        path: 'supervisor/financial-setup/periods/add-period-set',
        menuItemId: 9361,
        overrideAspxPageName: 'periodDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/currency',
        menuItemId: 9370,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/currency/add-currency',
        menuItemId: 9371,
        overrideAspxPageName: 'currencyDetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/countries',
        menuItemId: 9380,
        fixedHeight: true
      },
      {
        path: 'supervisor/financial-setup/countries/edit-country-codes',
        menuItemId: 9381,
        overrideAspxPageName: 'countrydetail.aspx'
      },
      {
        path: 'supervisor/financial-setup/time-billing',
        menuItemId: 2210,
        redirectTo: 'supervisor/financial-setup/time-billing/create-bill-template'
      },
      {
        path: 'supervisor/financial-setup/time-billing/create-bill-template',
        menuItemId: 2210
      },
      {
        path: 'supervisor/financial-setup/financial-periods',
        menuItemId: 9700,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup',
        menuItemId: 9400
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-actions',
        menuItemId: 19410,
        overrideAspxPageName: 'DossierActions.aspx'
      },
      {
        path: 'supervisor/case-management-setup/edit-custom-data-page-actions',
        menuItemId: 19420,
        overrideAspxPageName: 'DossierActionEdit.aspx'
      },
      {
        path: 'supervisor/case-management-setup/link-actions-to-pages',
        menuItemId: 19480,
        overrideAspxPageName: 'dossPageLinkAction.aspx'
      },
      {
        path: 'supervisor/case-management-setup/kpi-details',
        menuItemId: 9288,
        fixedHeight: true,
        overrideAspxPageName: 'kpiDetailsGrid.aspx'
      },
      {
        path: 'supervisor/case-management-setup/kpi-details/kpi-details',
        menuItemId: 9289,
        overrideAspxPageName: 'kpidetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/kpi-details/link-users',
        menuItemId: 9287,
        overrideAspxPageName: 'kpiLinkUser.aspx'
      },
      {
        path: 'supervisor/case-management-setup/kpi-details/kpi-master-users/link-fee-earner',
        menuItemId: 9907
      },
      {
        path: 'supervisor/case-management-setup/kpi-details/kpi-master-users',
        menuItemId: 9908,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/debt-recovery',
        menuItemId: 9410,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/debt-recovery/add-posting-type',
        menuItemId: 9411,
        overrideAspxPageName: 'debtPostTypeDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/debt-recovery/add-debt-scale',
        menuItemId: 9412,
        overrideAspxPageName: 'debtScaleDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/debt-recovery/credit-history',
        menuItemId: 9413,
        overrideAspxPageName: 'debtCreditHistoryGrid.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expert-types',
        menuItemId: 9420,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/expert-types/link-letters',
        menuItemId: 9424,
        overrideAspxPageName: 'exptypeLink.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expert-types/print-expert-types',
        menuItemId: 9425,
        overrideAspxPageName: 'exptypePrint.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expert-types/add-expert-type',
        menuItemId: 9421,
        overrideAspxPageName: 'exptypeDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expertise-areas',
        menuItemId: 9426,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/expertise-areas/add-or-edit-area-of-expertise',
        menuItemId: 9427,
        overrideAspxPageName: 'exprtiseDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expertise-areas/edit-area-of-expertise',
        menuItemId: 9428,
        overrideAspxPageName: 'exprtiseDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/expertise-areas/delete-area-of-expertise',
        menuItemId: 9429,
        overrideAspxPageName: 'exprtiseDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/key-date-types',
        menuItemId: 9430,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/key-date-types/add-key-date-type',
        menuItemId: 9431,
        overrideAspxPageName: 'kdtypeDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/key-date-types/batch-key-date-ticking',
        menuItemId: 9435,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/formulae',
        menuItemId: 9440,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/formulae/edit-formula',
        menuItemId: 9441,
        overrideAspxPageName: 'ssmFormulasEditEquation.aspx'
      },
      {
        path: 'supervisor/case-management-setup/formulae/add-formula',
        menuItemId: 9442,
        overrideAspxPageName: 'ssmFormulasDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/formulae/asks',
        menuItemId: 9446,
        overrideAspxPageName: 'ssmAskStatementsGrid.aspx'
      },
      {
        path: 'supervisor/case-management-setup/formulae/asks/add-ask',
        menuItemId: 9447,
        overrideAspxPageName: 'ssmAskStatementsDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/standard-documents',
        menuItemId: 9450,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/add-or-edit-standard-documents',
        menuItemId: 9451
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/charge-rates',
        menuItemId: 9455
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/add-current-document',
        menuItemId: 9456
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/page-field-links',
        menuItemId: 9505,
        overrideAspxPageName: 'dosspagefielddelete.aspx'
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/letter-link-fields',
        menuItemId: 9506
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/preview-letter',
        menuItemId: 9507,
        overrideAspxPageName: 'docsPreview.aspx'
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/batch-aged-debtor-letters',
        menuItemId: 9458
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/letter-email-links',
        menuItemId: 9459,
        overrideAspxPageName: 'LetterLinkEmail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/lexis-forms-config',
        menuItemId: 19460
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/lexis-forms-config/add-lexis-forms-config',
        menuItemId: 19461,
        overrideAspxPageName: 'LexisFormsConfigAdd.aspx'
      },
      {
        path: 'supervisor/case-management-setup/standard-documents/lexis-forms-config/lexis-forms-config-fields',
        menuItemId: 19462,
        overrideAspxPageName: 'LexisFormsFieldsConfig.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows',
        menuItemId: 9460,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/workflows/workflow-sections',
        menuItemId: 9508,
        overrideAspxPageName: 'WorkflowSections.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/workflow-section-details',
        menuItemId: 9509,
        overrideAspxPageName: 'WorkflowSectionDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/workflow-section-link-tasks',
        menuItemId: 9510,
        overrideAspxPageName: 'WorkflowSectionManager.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/add-workflow',
        menuItemId: 9461,
        overrideAspxPageName: 'workflowDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/delete-workflow',
        menuItemId: 9463,
        overrideAspxPageName: 'workflowDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/print-workflows',
        menuItemId: 9465,
        overrideAspxPageName: 'workflowcopy.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/letter-criteria',
        menuItemId: 9466,
        overrideAspxPageName: 'actionfdletcritadd.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/task-link-designation',
        menuItemId: 9467,
        overrideAspxPageName: 'TaskLinkDesignation.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/link-tasks',
        menuItemId: 9468,
        overrideAspxPageName: 'WorkflowManager.aspx'
      },
      {
        path: 'supervisor/case-management-setup/workflows/link-actions',
        menuItemId: 9469,
        overrideAspxPageName: 'TaskManager.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks',
        menuItemId: 9470,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/tasks/create-task',
        menuItemId: 9471,
        overrideAspxPageName: 'taskdetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks/copy-task',
        menuItemId: 9474,
        overrideAspxPageName: 'taskcopy.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks/keydate-action-link-task',
        menuItemId: 9475,
        overrideAspxPageName: 'actionLinkTask.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks/letter-charge-rates',
        menuItemId: 9476,
        overrideAspxPageName: 'letterrate.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks/tasks-links',
        menuItemId: 9477,
        overrideAspxPageName: 'workflowreorder.aspx'
      },
      {
        path: 'supervisor/case-management-setup/tasks/charge-rates',
        menuItemId: 9478,
        overrideAspxPageName: 'letterlinkdata.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages',
        menuItemId: 9480,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/add-custom-data-page',
        menuItemId: 9481,
        overrideAspxPageName: 'dosspageDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/link-work-types',
        menuItemId: 9485,
        overrideAspxPageName: 'dosspagelinkwt.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/link-fields-to-pages',
        menuItemId: 9487,
        overrideAspxPageName: 'dossPageLinkField.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/action-link-designation',
        menuItemId: 9488,
        overrideAspxPageName: 'ActionLinkDesignation.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/letter-rates',
        menuItemId: 9489,
        overrideAspxPageName: 'letterwcode.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields',
        menuItemId: 9490,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields/add-custom-data-page-fields',
        menuItemId: 9491,
        overrideAspxPageName: 'dossfieldDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields/edit-custom-data-page-fields',
        menuItemId: 9492,
        overrideAspxPageName: 'dossfieldEdit.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields/custom-data-page-list-fields',
        menuItemId: 9495,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields/delete-custom-data-page-fields',
        menuItemId: 9496,
        overrideAspxPageName: 'dosslistdelete.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-page-fields/view-linked-letters',
        menuItemId: 9494,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/custom-data-page-preview',
        menuItemId: 9502,
        overrideAspxPageName: 'dossierpagepreview.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/custom-data-page-design',
        menuItemId: 9503,
        overrideAspxPageName: 'dosspagedesign.aspx'
      },
      {
        path: 'supervisor/case-management-setup/custom-data-pages/custom-data-lists',
        menuItemId: 9504,
        overrideAspxPageName: 'dosslistdetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/email-templates',
        menuItemId: 9915,
        overrideAspxPageName: 'EmailTemplates.aspx',
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/email-templates/add-email-template',
        menuItemId: 9916,
        overrideAspxPageName: 'EmailTemplateDetail.aspx',
        iframeVisible: false,
        element: <RedirectToOsprey1Module url={`/main/EmailTemplateDetail.aspx?Area=9000&Flag=9916`} />
      },
      {
        path: 'supervisor/case-management-setup/email-templates/charge-rates-fixed',
        menuItemId: 9917,
        overrideAspxPageName: 'EmailTemplateLink.aspx'
      },
      {
        path: 'supervisor/case-management-setup/email-templates/charge-rates-hourly',
        menuItemId: 9918,
        overrideAspxPageName: 'EmailTemplateRate.aspx'
      },
      {
        path: 'supervisor/case-management-setup/email-templates/charge-rates-fixed-link-data',
        menuItemId: 9919,
        overrideAspxPageName: 'EmailTemplateLinkData.aspx'
      },
      {
        path: 'supervisor/case-management-setup/email-templates/rates-hourly-base',
        menuItemId: 9920,
        overrideAspxPageName: 'EmailTemplateWCode.aspx'
      },
      {
        path: 'supervisor/case-management-setup/matter-history/type',
        menuItemId: 9931,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/matter-history/audit',
        menuItemId: 9932
      },
      {
        path: 'supervisor/case-management-setup/bundle-document-settings',
        menuItemId: 19463
      },
      {
        path: 'supervisor/case-management-setup/gdpr/retention-policies',
        menuItemId: 19491
      },
      {
        path: 'supervisor/case-management-setup/gdpr/retention-policies-details',
        menuItemId: 19492,
        overrideAspxPageName: 'retentionPoliciesAddEdit.aspx'
      },
      {
        path: 'supervisor/case-management-setup/gdpr/disposition',
        menuItemId: 19493
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/questionnaire-administrator',
        menuItemId: 9560,
        fixedHeight: true
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/add-worktype-administrator',
        menuItemId: 9561,
        overrideAspxPageName: 'qsWorktypeAdminsDetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/view-questionnaires',
        menuItemId: 9570,
        fixedHeight: true,
        overrideAspxPageName: 'clientviewgrid.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/add-view',
        menuItemId: 9571,
        overrideAspxPageName: 'clientviewdetail.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/edit-view',
        menuItemId: 9572,
        overrideAspxPageName: 'clientviewedit.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/clone-view',
        menuItemId: 9570,
        overrideAspxPageName: 'clientviewedit.aspx',
        aspxUrlParams: { clone: 1 }
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/delete-view',
        menuItemId: 9573,
        overrideAspxPageName: 'clientviewdelete.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/linked-fields',
        menuItemId: 9574,
        overrideAspxPageName: 'ClientViewLinkField.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/view-design',
        menuItemId: 9575,
        overrideAspxPageName: 'clientviewDesign.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/view-preview',
        menuItemId: 9576,
        overrideAspxPageName: 'clientviewPreview.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/view-link-designation',
        menuItemId: 9577,
        overrideAspxPageName: 'ClientViewLinkDesignation.aspx'
      },
      {
        path: 'supervisor/case-management-setup/questionnaire-design/published-views-in-use',
        menuItemId: 9578,
        fixedHeight: true
      },
      {
        path: 'supervisor/compliance',
        menuItemId: 12002,
        redirectTo: 'supervisor/compliance/colp-cofa-list-management'
      },
      {
        path: 'supervisor/compliance/colp-cofa-list-management',
        menuItemId: 12002,
        fixedHeight: true
      },
      {
        path: 'supervisor/integrations',
        menuItemId: 9400
      },
      {
        path: 'supervisor/integrations/searchflow-setup',
        menuItemId: 12003
      },
      {
        path: 'supervisor/integrations/searchflow-setup/searchflow-bundles',
        menuItemId: 12005
      },
      {
        path: 'supervisor/integrations/infotrack-mapping',
        menuItemId: 19501
      },
      {
        path: 'supervisor/integrations/perfectportal-mapping',
        menuItemId: 19502
      },
      {
        path: 'supervisor/integrations/perfectportal-defaults',
        menuItemId: 19502,
        iframeVisible: false,
        element: <PerfectPortalCaseTypeMapping />
      },
      {
        path: 'supervisor/integrations/coadjute-tasks',
        menuItemId: 19503
      },
      {
        path: 'supervisor/integrations/formevo-configuration',
        menuItemId: 9400,
        iframeVisible: false,
        element: <FormEvoConfiguration />
      },
      {
        path: 'supervisor/crm',
        menuItemId: 9000
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates',
        menuItemId: 9000
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-crown-courts-detail',
        menuItemId: 9864,
        overrideAspxPageName: 'advCrownCourtsDetail.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-police-schemes-detail',
        menuItemId: 9870,
        overrideAspxPageName: 'cdsPoliceSchemesDetail.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-police-stations-detail',
        menuItemId: 9871,
        overrideAspxPageName: 'cdsPoliceStationsDetail.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-prison-law-fees-detail',
        menuItemId: 9872,
        overrideAspxPageName: 'cdsPrisonLawFeesDetail.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-schemes-detail',
        menuItemId: 9873,
        overrideAspxPageName: 'cdsSchemesDetail.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-crown-courts',
        menuItemId: 9874,
        overrideAspxPageName: 'advCrownCourtsGrid.aspx'
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-police-schemes',
        menuItemId: 9880
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-police-stations',
        menuItemId: 9881
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-prison-law-fees',
        menuItemId: 9882
      },
      {
        path: 'supervisor/crm/crm-af1-additional-rates/crm-schemes',
        menuItemId: 9883,
        fixedHeight: true
      },
      {
        path: 'supervisor/crm/crm-auto-posting-defaults',
        menuItemId: 9884,
        fixedHeight: true
      },
      {
        path: 'supervisor/crm/reprint-crm611-spreadsheet',
        menuItemId: 9885,
        fixedHeight: true
      },
      {
        path: 'supervisor/crm/manage-lists',
        menuItemId: 3820
      },
      {
        path: 'supervisor/crm/crm-list-defaults',
        menuItemId: 3821
      },
      {
        path: 'supervisor/include-documents',
        menuItemId: 9900,
        fixedHeight: true
      },
      {
        path: 'supervisor/include-documents/edit-include-documents',
        menuItemId: 9901,
        overrideAspxPageName: 'subLetterDetail.aspx'
      },
      {
        path: 'supervisor/include-documents/include-documents',
        menuItemId: 9902,
        overrideAspxPageName: 'subletterlink.aspx'
      },
      {
        path: 'case-management/vat-mtd-return-form',
        menuItemId: 19497,
        overrideAspxPageName: 'VATMTDReturnForm.aspx'
      },
      {
        path: 'case-management/vat-form-submission',
        menuItemId: 19498,
        overrideAspxPageName: 'VATFormSubmission.aspx'
      },
      {
        path: 'case-management/conveyancer-portal',
        menuItemId: 9903,
        overrideAspxPageName: 'XIT2Import.aspx'
      },
      {
        path: 'supervisor/case-management-setup/set-up-conveyancer-portal',
        menuItemId: 9904,
        overrideAspxPageName: 'XIT2Setup.aspx'
      },
      {
        path: 'supervisor/case-management-setup/import-xml-file',
        menuItemId: 9905,
        overrideAspxPageName: 'XMLImportClients.aspx'
      },
      {
        path: 'supervisor/case-management-setup/adjust-workflow-dates',
        menuItemId: 9906,
        overrideAspxPageName: 'matttaskAdjustDate.aspx'
      },
      {
        path: 'supervisor/portal-setup',
        menuItemId: 9912
      },
      {
        path: 'supervisor/portal-setup/associate-access',
        menuItemId: 9810,
        fixedHeight: true
      },
      {
        path: 'supervisor/portal-setup/associate-access/add-associate',
        menuItemId: 9811,
        overrideAspxPageName: 'pubassocdetail.aspx'
      },
      {
        path: 'supervisor/portal-setup/conveyancing-setup-council-rates',
        menuItemId: 9821,
        overrideAspxPageName: 'councilareaGrid.aspx'
      },
      {
        path: 'supervisor/portal-setup/conveyancing-setup-conveyancing-rates',
        menuItemId: 9822,
        overrideAspxPageName: 'conveyrateGrid.aspx'
      },
      {
        path: 'supervisor/portal-setup/conveyancing-setup-conveyancing-rates-band',
        menuItemId: 9823,
        overrideAspxPageName: 'conveyBandPage.aspx'
      },
      {
        path: 'supervisor/portal-setup/conveyancing-setup-conveyancing-rates-detail',
        menuItemId: 9824,
        overrideAspxPageName: 'conveyRatePage.aspx'
      },
      {
        path: 'supervisor/portal-setup/conveyancing-setup-council-rate-add',
        menuItemId: 9825,
        overrideAspxPageName: 'councilareaDetail.aspx'
      },
      {
        path: 'supervisor/portal-setup/web-publishing-info',
        menuItemId: 9830,
        overrideAspxPageName: 'pubinfogrid.aspx'
      },
      {
        path: 'supervisor/portal-setup/web-publishing-info-details',
        menuItemId: 9832,
        overrideAspxPageName: 'pubinfodetail.aspx'
      },
      {
        path: 'supervisor/portal-setup/general-messages',
        menuItemId: 9840,
        overrideAspxPageName: 'GeneralMessagesGrid.aspx'
      },
      {
        path: 'supervisor/portal-setup/general-messages-detail',
        menuItemId: 9843,
        overrideAspxPageName: 'GeneralMessagesDetail.aspx'
      },
      {
        path: 'supervisor/portal-setup/portal-new-page-setup',
        menuItemId: 9913
      },
      {
        path: 'supervisor/portal-setup/access-templates-setup',
        menuItemId: 9921,
        fixedHeight: true
      },
      {
        path: 'supervisor/portal-setup/access-templates-setup/web-portal-access-edit',
        menuItemId: 9922,
        overrideAspxPageName: 'webPortalAccessEdit.aspx'
      },
      {
        path: 'supervisor/portal-setup/access-templates-setup/web-portal-access-template',
        menuItemId: 9923,
        overrideAspxPageName: 'webPortalAccessTemplate.aspx'
      },
      {
        path: 'supervisor/portal-setup/document-view-setup',
        menuItemId: 9924
      },
      {
        path: 'supervisor/portal-setup/configuration',
        menuItemId: 9927
      },
      {
        path: 'supervisor/smart-actions',
        menuItemId: 9941,
        fixedHeight: true
      },
      {
        path: 'supervisor/smart-actions/timesheet',
        menuItemId: 10001,
        fixedHeight: true
      },
      {
        path: 'supervisor/smart-actions/timesheet/pending-data',
        menuItemId: 10002,
        fixedHeight: true
      },
      {
        path: 'supervisor/smart-actions/auto-billing',
        menuItemId: 10003,
        fixedHeight: true
      },
      {
        path: 'supervisor/smart-actions/client-matter-data-import',
        menuItemId: 10004,
        fixedHeight: true
      },
      {
        path: 'supervisor/smart-actions/matter-history-note',
        menuItemId: 10007,
        fixedHeight: true
      },
      {
        path: 'time-recording',
        redirectTo: 'time-recording/time-ledger'
      },
      {
        path: 'time-recording/time-ledger',
        menuItemId: 2200,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'timerecpage.aspx'
      },
      {
        path: 'time-recording/time-ledger/undo-time-entry',
        menuItemId: 2120,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'timeledgundo.aspx'
      },
      {
        path: 'time-recording/time-ledger/attendance-note',
        menuItemId: 2131,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'attnotedetail.aspx'
      },
      {
        path: 'time-recording/prospect-time-recording',
        menuItemId: 2250,
        pageIsMatterSpecific: true,
        overrideAspxPageName: 'timerecpage.aspx',
        aspxUrlParams: [{ key: 'type', value: 'p' }]
      },
      {
        path: 'time-recording/time-sheets',
        menuItemId: 2800
      },
      {
        path: 'time-recording/time-sheets/new-time-sheet',
        menuItemId: 2810
      },
      {
        path: 'time-recording/time-sheets/timeline-detail',
        menuItemId: 2820
      },
      {
        path: 'time-recording/tick-billed-time',
        menuItemId: 2300
      },
      {
        path: 'time-recording/tick-billed-time/time-allocation',
        menuItemId: 2310,
        overrideAspxPageName: 'tbillallo.aspx'
      },
      {
        path: 'time-recording/write-off',
        menuItemId: 2500
      },
      {
        path: 'time-recording/write-off/view-written-off-time',
        menuItemId: 2520
      },
      {
        path: 'time-recording/print-ledger',
        menuItemId: 2700
      },
      {
        path: 'time-recording/print-ledger/running-record-of-costs-report',
        menuItemId: 2710
      },
      {
        path: 'time-recording/fee-earner-summary',
        menuItemId: 2100
      },
      {
        path: 'time-recording/mark-ups',
        menuItemId: 2400,
        pageIsMatterSpecific: true
      },
      {
        path: 'time-recording/mark-ups/mark-up-time',
        menuItemId: 2410,
        pageIsMatterSpecific: true
      },
      {
        path: 'time-recording/transfer-time',
        menuItemId: 2002,
        pageIsMatterSpecific: true
      },
      {
        path: 'time-recording/transfer-time/transferred-time',
        menuItemId: 2003,
        pageIsMatterSpecific: true
      },
      {
        path: 'time-recording/phase-limits',
        menuItemId: 2960,
        pageIsMatterSpecific: true
      },
      {
        path: 'matters-for-review',
        menuItemId: 30,
        fixedHeight: true
      },
      {
        path: 'global-search/advanced-search',
        menuItemId: 31,
        fixedHeight: true
      },
      {
        path: 'global-search',
        menuItemId: 32,
        fixedHeight: true
      },
      {
        path: 'my-account/account-settings',
        menuItemId: 90
      },
      {
        path: 'my-account/expense-claims',
        menuItemId: 14110,
        iframeVisible: false,
        element: <ExpensesOvertimeModule isSubmissionVersion={true} selectedTab="expenses" />
      },
      {
        path: 'my-account/overtime-claims',
        menuItemId: 14210,
        iframeVisible: false,
        element: <ExpensesOvertimeModule isSubmissionVersion={true} selectedTab="overtime" />
      },
      {
        path: 'support/request-1-1-support',
        iframeVisible: false,
        element: <Support />
      }
    ]
  },
  {
    path: 'auth',
    element: <RootLayout />,
    children: [
      {
        path: 'login',
        element: <Login />,
        overrideAspxPageName: 'LogoutSuccess.html'
      },
      {
        // react logout
        path: 'logout',
        element: <Logout />
      },
      {
        path: 'request-password-reset',
        element: <RequestPasswordReset />
      },
      {
        path: 'reset-password',
        element: <ResetPassword />
      }
    ]
  }
]

// add default properties to a route if not set, i.e. error boundary and iframe page
const decorateIndividualRoute = (route) => {
  // add an id so we can access these details using useRouteLoaderData
  route.id = route.id || route.path.replace('?', '')

  if (!Object.keys(route).includes('iframeVisible')) route.iframeVisible = true
  if (!route.element) route.element = <Fragment />
  if (!route.errorElement) route.errorElement = <PageErrorBoundary />
  if (!route.loader) route.loader = () => route

  return route
}

// perform the decoration to each item in the routes array, and its children
const decoratedRoutes = routes.map((route) => {
  route = decorateIndividualRoute(route)

  if (route.children) {
    route.children = route.children.map((child) => decorateIndividualRoute(child))
  }

  return route
})

export const getRoutesByKey = () => {
  const routesByMenuItemId = {}
  const routesByPath = {}
  const allRoutesByMenuItemId = {}

  routes.forEach((route) => {
    routesByMenuItemId[route.menuItemId] = route
    routesByPath[route.path.replace('?', '')] = route

    if (!allRoutesByMenuItemId[route.menuItemId]) {
      allRoutesByMenuItemId[route.menuItemId] = []
    }
    allRoutesByMenuItemId[route.menuItemId].push(route)

    if (route.children) {
      route.children.forEach((childRoute) => {
        routesByMenuItemId[childRoute.menuItemId] = childRoute
        routesByPath[childRoute.path.replace('?', '')] = childRoute

        if (!allRoutesByMenuItemId[childRoute.menuItemId]) {
          allRoutesByMenuItemId[childRoute.menuItemId] = []
        }
        allRoutesByMenuItemId[childRoute.menuItemId].push(childRoute)
      })
    }
  })

  return { routesByMenuItemId, routesByPath, allRoutesByMenuItemId }
}

export const { routesByMenuItemId, routesByPath, allRoutesByMenuItemId } = getRoutesByKey()
export default decoratedRoutes
